import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFocusMode } from '../context/FocusModeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { ActionPlanCard, ScholarshipCard } from '../components/ExpandableCards';
import { FocusModeBanner } from '../components/FocusMode';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import GuidedTour, { useTour } from '../components/GuidedTour';
import { useActiveChild } from '../context/ActiveChildContext';
import { 
  Zap, TrendingUp, GraduationCap, DollarSign, Target, 
  Calendar, BookOpen, ArrowRight, CheckCircle, CheckCircle2, Clock,
  Briefcase, FileText, RefreshCw, Brain, Shield, Award,
  ClipboardCheck, Phone, Plus, Upload, Star, Eye, AlertTriangle,
  Building, Sparkles, User, Archive, Trash2, ChevronRight, Tag,
  GripVertical, X, Wallet, PiggyBank, Calculator, ShieldAlert, HelpCircle, Settings, Circle,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { showFriendlyError } from '../utils/friendlyErrors';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ReportGenerator from '../components/ReportGenerator';
import NewsFeed from '../components/NewsFeed';
import { PathTourInvite } from '../components/PathWelcomeStrip';
import { NudgePanel } from '../components/NudgePanel';
import ReadinessMini from '../components/ReadinessMini';
import MissionControl from '../components/dashboard/MissionControl';
import PatriciaPageGuide from '../components/PatriciaPageGuide';
import WorkforcePellCallout from '../components/WorkforcePellCallout';
import PlanNextActions from '../components/plan/PlanNextActions';
import WelcomeHeader from '../components/dashboard/WelcomeHeader';
import NCCDPHeroCard from '../components/NCCDPHeroCard';
import EvolutionCard from '../components/EvolutionCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps profile-strength fields → friendly label, where to complete it, and destination.
const FRIENDLY_FIELD = {
  full_name: { label: 'Your name', where: 'Settings', to: '/app/profile' },
  bio: { label: 'Short bio', where: 'Settings', to: '/app/profile' },
  state: { label: 'State', where: 'Settings', to: '/app/profile' },
  city: { label: 'City', where: 'Settings', to: '/app/profile' },
  grade_level: { label: 'Grade level', where: 'Settings', to: '/app/profile' },
  gpa: { label: 'GPA', where: 'Settings', to: '/app/profile' },
  interests: { label: 'Interests', where: 'Settings', to: '/app/profile' },
  skills: { label: 'Skills', where: 'Settings', to: '/app/profile' },
  extracurriculars: { label: 'Extracurriculars', where: 'Settings', to: '/app/profile' },
  career_aspirations: { label: 'Career goal', where: 'Settings', to: '/app/profile' },
  test_scores: { label: 'Test scores (SAT/ACT)', where: 'Settings', to: '/app/profile' },
  transcript: { label: 'Transcript', where: 'Document Vault', to: '/app/vault' },
  certifications: { label: 'Certifications', where: 'Settings', to: '/app/profile' },
  documents: { label: 'Documents', where: 'Document Vault', to: '/app/vault' },
};

// Goal categories with icons
const CATEGORIES = {
  career: { label: 'Career', icon: Briefcase, color: 'blue' },
  college: { label: 'College', icon: GraduationCap, color: 'purple' },
  scholarship: { label: 'Scholarship', icon: DollarSign, color: 'green' },
  skill: { label: 'Skill', icon: Zap, color: 'amber' },
  certification: { label: 'Certification', icon: BookOpen, color: 'cyan' },
  personal: { label: 'Personal', icon: Star, color: 'pink' },
};

const COLUMNS = [
  { id: 'recommended', label: 'To Do', icon: Archive, color: 'slate', statuses: ['recommended', 'ready', 'backlog'] },
  { id: 'in_progress', label: 'In Progress', icon: Clock, color: 'blue', statuses: ['in_progress', 'waiting', 'attention'] },
  { id: 'completed', label: 'Done', icon: CheckCircle, color: 'green', statuses: ['completed', 'done'] },
];

// Currency formatter
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// ─── Institutional Student Plan Card ────────────────────────────────────────
// Only rendered for students with organization_id (WCPSS / institutional).
// B2C students never see this component.
function InstitutionalPlanCard() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('futurepath_token');
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/nc/school-plan-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPlan(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !plan?.is_institutional) return null;

  const reqItems = [
    { label: 'Assessment',    done: plan.has_assessment },
    { label: 'Career Goal',   done: !!plan.career_goal },
    { label: 'CTE Pathway',   done: !!plan.cte_pathway },
    { label: 'Course Plan',   done: plan.has_course_plan },
    { label: `WBL (${plan.wbl_goal}h)`, done: (plan.wbl_hours || 0) >= (plan.wbl_goal || 40) },
    { label: 'CDP Sections',  done: (plan.sections_complete || 0) >= 2 },
    { label: 'Parent Ack.',   done: plan.parent_acknowledged },
  ];
  const doneCt = reqItems.filter(r => r.done).length;
  const allDone = doneCt === reqItems.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
      className="mb-4 rounded-2xl border border-cyan-500/20 bg-[#0a1729] overflow-hidden"
      data-testid="institutional-plan-card">

      {/* Identity strip, school / grade / counselor */}
      <div className="px-5 py-4 border-b border-white/[0.06]"
        style={{ background: 'linear-gradient(135deg, rgba(6,22,40,0.9) 0%, rgba(3,14,28,0.95) 100%)' }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Left: school + grade + counselor */}
          <div className="flex flex-wrap items-center gap-4">
            {plan.school_name && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-500/25 bg-cyan-500/[0.08]">
                  <Building className="h-4 w-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-cyan-400/70">School</p>
                  <p className="text-sm font-bold text-white leading-tight" data-testid="student-school-name">{plan.school_name}</p>
                </div>
              </div>
            )}
            {plan.grade && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-violet-500/25 bg-violet-500/[0.08]">
                  <GraduationCap className="h-4 w-4 text-violet-400" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-violet-400/70">Grade</p>
                  <p className="text-sm font-bold text-white leading-tight" data-testid="student-grade">{plan.grade_display || `Grade ${plan.grade}`}</p>
                </div>
              </div>
            )}
            {plan.counselor_name && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-500/25 bg-emerald-500/[0.08]">
                  <Users className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400/70">Counselor</p>
                  <p className="text-sm font-bold text-white leading-tight" data-testid="student-counselor">{plan.counselor_name}</p>
                </div>
              </div>
            )}
          </div>
          {/* Right: requirements count + view link */}
          <div className="flex items-center gap-3">
            <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold ${
              allDone
                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'
                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            }`}>{doneCt}/{reqItems.length} requirements</span>
            <Link to="/app/my-school-plan"
              className="flex items-center gap-1 text-[11.5px] font-medium text-cyan-300 hover:text-cyan-200 transition-colors">
              Full plan <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* Progress pills */}
      <div className="px-5 py-3 flex flex-wrap gap-2">
        {plan.cte_pathway && (
          <span className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-cyan-500/[0.08] border border-cyan-500/20 text-cyan-300">
            <Award className="h-3 w-3" /> {plan.cte_pathway}
          </span>
        )}
        <span className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.07] text-[#C6D4E8]">
          <FileText className="h-3 w-3" /> CDP {plan.overall_completion}% · {plan.sections_complete}/{plan.total_sections} sections
        </span>
        <span className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
          (plan.wbl_hours || 0) >= (plan.wbl_goal || 40)
            ? 'bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-300'
            : 'bg-amber-500/[0.08] border-amber-500/20 text-amber-300'
        }`}>
          <Briefcase className="h-3 w-3" /> WBL {plan.wbl_hours || 0}h / {plan.wbl_goal || 40}h
        </span>
      </div>
    </motion.div>
  );
}

// District Requirements Tab, full inline school plan for the student dashboard tab
function DistrictRequirementsTab() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('futurepath_token');
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/nc/school-plan-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPlan(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
    </div>
  );

  if (!plan?.is_institutional) return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-8 text-center">
      <Building className="h-8 w-8 text-slate-300 mx-auto mb-3" />
      <p className="text-sm text-[#C6D4E8]">District requirements are not configured for your account.</p>
    </div>
  );

  const REQ = [
    { label: 'Career Assessment',     done: plan.has_assessment,                           desc: 'Complete the Holland / RIASEC career assessment',         icon: Brain },
    { label: 'Career Goal',           done: !!plan.career_goal,                            desc: `Set career direction${plan.career_goal ? `, ${plan.career_goal}` : ''}`, icon: Target },
    { label: 'CTE Pathway',           done: !!plan.cte_pathway,                            desc: `Select a CTE cluster${plan.cte_pathway ? `, ${plan.cte_pathway}` : ''}`, icon: Award },
    { label: 'Course Plan',           done: plan.has_course_plan,                          desc: 'Create a 4-year course plan aligned to your pathway',     icon: FileText },
    { label: `WBL Hours (${plan.wbl_goal}h goal)`, done: (plan.wbl_hours||0) >= (plan.wbl_goal||40), desc: `Work-based learning, ${plan.wbl_hours||0}h logged of ${plan.wbl_goal||40}h required`, icon: Briefcase },
    { label: 'CDP Progress',          done: (plan.sections_complete||0) >= 2,              desc: `Career Development Plan, ${plan.sections_complete} of ${plan.total_sections} sections complete`, icon: TrendingUp },
    { label: 'Parent/Guardian Review',done: plan.parent_acknowledged,                      desc: 'Parent/guardian has reviewed and acknowledged the plan',   icon: Users },
  ];
  const done = REQ.filter(r => r.done).length;
  const pct = Math.round((done / REQ.length) * 100);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">

      {/* ── Intro Banner, brand cyan glow, replaces the old purple/indigo tone ── */}
      <div className="relative overflow-hidden rounded-2xl border border-cyan-400/25 bg-[#0a1729]/70 p-6"
        style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.55)' }}>
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-20 h-52 w-52 rounded-full blur-3xl opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.4), transparent 65%)' }} />
        <div className="relative flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-cyan-400/40 bg-cyan-500/[0.1]">
            <Building className="h-5 w-5 text-cyan-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-cyan-300 mb-1">
              {plan.district || 'Wake County Public School System'}
            </p>
            <h2 className="text-base font-bold text-white mb-1">District Requirements</h2>
            <p className="text-[12.5px] text-[#C6D4E8] leading-relaxed max-w-2xl">
              Your school district has defined specific milestones that every student must complete as part of their
              Career Development Plan. These requirements are set by <span className="text-slate-300 font-medium">{plan.school_name}</span> and
              aligned to North Carolina graduation standards. Complete each item below to fulfil your district's expectations and stay on track for graduation.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <span className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/25 text-cyan-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> Fulfil all {REQ.length} requirements to meet district standards
          </span>
          <Link
            to="/app/counselor-meetings"
            data-testid="counselor-review-link"
            className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-violet-200 hover:bg-violet-500/20 hover:text-white transition-colors"
          >
            <Users className="h-3.5 w-3.5" /> Your counselor reviews your progress each semester, book time →
          </Link>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-2xl border border-cyan-500/20 bg-[#0a1729] p-6"
        style={{ background: 'linear-gradient(135deg, rgba(6,22,40,0.95) 0%, rgba(3,14,28,0.98) 100%)' }}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-cyan-400 mb-1">
              {plan.district || 'Wake County Public School System'}
            </p>
            <h2 className="text-xl font-bold text-white">{plan.school_name}</h2>
            <p className="text-sm text-[#C6D4E8] mt-0.5">Career Development Plan Requirements</p>
          </div>
          <div className="text-right">
            <p className={`text-3xl font-black ${pct === 100 ? 'text-emerald-400' : 'text-cyan-300'}`}>{pct}%</p>
            <p className="text-[11px] text-[#C6D4E8]">{done} of {REQ.length} complete</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden">
          <motion.div
            initial={{ width: 0 }} animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="h-2 rounded-full"
            style={{ background: pct === 100 ? '#34d399' : 'linear-gradient(90deg, #22d3ee, #a78bfa)' }} />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="rounded-2xl border border-white/[0.07] bg-[#0b1b30] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.05]">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#C6D4E8]">Requirements</p>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {REQ.map((r, i) => (
            <div key={i} className={`flex items-center gap-4 px-5 py-4 transition-colors ${r.done ? '' : 'hover:bg-white/[0.015]'}`}>
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                r.done
                  ? 'border-emerald-500/30 bg-emerald-500/[0.08]'
                  : 'border-amber-500/25 bg-amber-500/[0.05]'
              }`}>
                <r.icon className={`h-4 w-4 ${r.done ? 'text-emerald-400' : 'text-amber-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${r.done ? 'text-white' : 'text-slate-300'}`}>{r.label}</p>
                <p className="text-[11.5px] text-slate-300 mt-0.5 truncate">{r.desc}</p>
              </div>
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                r.done ? 'bg-emerald-500/20' : 'bg-white/[0.04]'
              }`}>
                {r.done
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  : <Circle className="h-4 w-4 text-slate-300" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* WBL Progress */}
      <div className="rounded-2xl border border-white/[0.07] bg-[#0b1b30] p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-white">Work-Based Learning</p>
          <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold border ${
            (plan.wbl_hours||0) >= (plan.wbl_goal||40)
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          }`}>{plan.wbl_hours||0}h / {plan.wbl_goal||40}h</span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden mb-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, ((plan.wbl_hours||0)/(plan.wbl_goal||40))*100)}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="h-2 rounded-full bg-amber-400" />
        </div>
        {(plan.wbl_experiences||[]).length > 0 && (
          <div className="space-y-1.5">
            {plan.wbl_experiences.slice(0,3).map((e, i) => (
              <div key={i} className="flex items-center justify-between text-[11.5px]">
                <span className="text-[#C6D4E8]">{e.employer} · {e.type}</span>
                <span className={`font-semibold ${e.verified ? 'text-emerald-400' : 'text-slate-300'}`}>{e.hours}h {e.verified ? '✓' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Link to="/app/my-school-plan"
          className="flex items-center gap-2 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.06]
            px-4 py-2.5 text-[12px] font-semibold text-cyan-300 hover:bg-cyan-400/[0.10] transition-all">
          View full school plan <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </motion.div>
  );
}



export default function DashboardPage() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const { isSelf, canWrite } = useActiveChild();
  const { focusModeEnabled } = useFocusMode();
  const { showTour, closeTour } = useTour('student');
  const location = useLocation();
  const navigate = useNavigate();

  // First login never lands on a raw dashboard, the Signature Welcome runs first.
  // Organization staff are not participants: they get their console, never the welcome.
  // And a parent viewing a teen who owns their own account is never pushed into that teen's setup.
  // Gen2 college students get their own dedicated dashboard.
  useEffect(() => {
    if (user?.student_type === 'college' || user?.welcome_archetype === 'college_student') {
      navigate('/app/college-dashboard', { replace: true });
      return;
    }
    if (user?.role === 'org_staff') { navigate('/app/org'); return; }
    if (!isSelf && !canWrite) return;
    // High school students / school-rostered students bypass the welcome flow,     // their district already provides the profile context.
    const isHighSchoolStudent = user?.role === 'student' && (
      user?.education_level === 'high_school' ||
      user?.learning_stage === 'high_school' ||
      user?.organization_id ||
      user?.school_id ||
      user?.current_sponsor_org_id
    );
    if (isHighSchoolStudent) return;
    axios.get(`${API}/welcome/state`, getAuthHeader())
      .then((r) => { if (!r.data.basics_complete || r.data.needs_archetype || r.data.should_launch) navigate('/app/welcome'); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, canWrite]);

  // First-visit greeting: show "Welcome" on the very first dashboard visit
  // (right after onboarding) and "Welcome back" for every subsequent session.
  // We persist the flag per-user in localStorage so a refresh shows the right
  // copy and a logout/login on a new device starts fresh.
  const isFirstVisit = useMemo(() => {
    if (!user?.id) return false;
    if (location.state?.firstVisit) return true;
    return !localStorage.getItem(`fp_dashboard_visited_${user.id}`);
  }, [user?.id, location.state]);

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`fp_dashboard_visited_${user.id}`, '1');
    }
  }, [user?.id]);

  const greeting = isFirstVisit ? 'Welcome' : 'Welcome back';
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [pathways, setPathways] = useState(null);
  const [actionPlan, setActionPlan] = useState(null);
  const [scholarships, setScholarships] = useState(null);
  const [colleges, setColleges] = useState(null);
  const [roadmap90Day, setRoadmap90Day] = useState(null);
  
  // Intelligence tabs state
  // Institutional students open on District Requirements, that is the page the district,
  // the counselor and the student all care about first.
  const [activeTab, setActiveTab] = useState(
    (user?.org_id || user?.organization_id) ? 'district' : 'overview'
  );
  const [profileStrength, setProfileStrength] = useState(null);
  const [showStrengthModal, setShowStrengthModal] = useState(false);
  const [careerMatches, setCareerMatches] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [security, setSecurity] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [testScores, setTestScores] = useState([]);
  const [personalitySummary, setPersonalitySummary] = useState(null);
  
  // Goals state
  const [goals, setGoals] = useState([]);
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [draggedGoal, setDraggedGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: 'personal',
    priority: 'medium',
    target_date: '',
    source: 'manual'
  });
  
  // Financial state
  const [financialData, setFinancialData] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [linkedParents, setLinkedParents] = useState([]);
  
  // Form states
  const [showAddCert, setShowAddCert] = useState(false);
  const [showAddScore, setShowAddScore] = useState(false);
  const [newCert, setNewCert] = useState({ name: '', issuer: '', date_earned: '' });
  const [newScore, setNewScore] = useState({ test_type: 'SAT', score: '', date_taken: '' });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [analyzingTranscript, setAnalyzingTranscript] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const headers = { ...getAuthHeader(), withCredentials: true };
      
      // Fetch all data in parallel
      const [
        dashboardRes, 
        userDataRes, 
        strengthRes, 
        timelineRes, 
        securityRes, 
        certsRes, 
        scoresRes, 
        personalityRes,
        goalsRes,
        financialRes,
        personalityDataRes
      ] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, headers),
        axios.get(`${API}/user-data`, headers),
        axios.get(`${API}/profile/strength`, headers),
        axios.get(`${API}/profile/progress-timeline`, headers),
        axios.get(`${API}/profile/security-info`, headers),
        axios.get(`${API}/certifications`, headers),
        axios.get(`${API}/test-scores`, headers),
        axios.get(`${API}/profile/personality-summary`, headers),
        axios.get(`${API}/goals`, headers),
        Promise.resolve({ data: null }),
        axios.get(`${API}/user-data/personality`, headers).catch(() => ({ data: null }))
      ]);
      
      setDashboardData(dashboardRes.data);
      
      // Load saved data
      const data = userDataRes.data;
      if (data.pathways?.data) setPathways(data.pathways.data);
      if (data.scholarships?.data) setScholarships(data.scholarships.data);
      if (data.action_plan?.data) setActionPlan(data.action_plan.data);
      if (data.colleges?.data) setColleges(data.colleges.data);
      if (data.roadmap_90day?.data) setRoadmap90Day(data.roadmap_90day.data);
      
      // Profile intelligence data
      setProfileStrength(strengthRes.data);
      setTimeline(timelineRes.data.timeline);
      setSecurity(securityRes.data);
      setCertifications(certsRes.data.certifications);
      setTestScores(scoresRes.data.test_scores);
      setPersonalitySummary(personalityRes.data);
      setGoals(goalsRes.data.goals || []);
      setFinancialData(financialRes.data);
      const pData = personalityDataRes?.data?.data || personalityDataRes?.data;
      setCareerMatches(Array.isArray(pData?.career_matches) ? pData.career_matches : null);
      
      // Fetch linked parents (for students)
      try {
        const parentsRes = await axios.get(`${API}/family/linked-parents`, headers);
        setLinkedParents(parentsRes.data.parents || []);
      } catch { /* Not a linked student - that's ok */ }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Goal functions
  const addGoal = async () => {
    if (!newGoal.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    try {
      const response = await axios.post(`${API}/goals`, {
        ...newGoal,
        status: 'recommended'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals([...goals, response.data]);
      window.dispatchEvent(new Event('goals-updated'));
      setShowAddGoalModal(false);
      setNewGoal({ title: '', description: '', category: 'personal', priority: 'medium', target_date: '', source: 'manual' });
      toast.success('Goal added to backlog!');
    } catch (error) {
      showFriendlyError(error, { context: 'Add goal', fallback: "We couldn't save your goal. Please try again." });
    }
  };

  const updateGoalStatus = async (goalId, newStatus) => {
    try {
      await axios.put(`${API}/goals/${goalId}/status`, { status: newStatus }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals(goals.map(g => g.id === goalId ? { ...g, status: newStatus } : g));
      window.dispatchEvent(new Event('goals-updated'));
      if (newStatus === 'completed') {
        toast.success('Goal completed! 🎉');
      }
    } catch (error) {
      showFriendlyError(error, { context: 'Update goal', fallback: "We couldn't update that goal. Please try again." });
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/goals/${goalId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals(goals.filter(g => g.id !== goalId));
      toast.success('Goal deleted');
    } catch (error) {
      showFriendlyError(error, { context: 'Delete goal', fallback: "We couldn't remove that goal. Please try again." });
    }
  };

  const handleDragStart = (goal) => setDraggedGoal(goal);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (columnId) => {
    if (draggedGoal && draggedGoal.status !== columnId) {
      updateGoalStatus(draggedGoal.id, columnId);
    }
    setDraggedGoal(null);
  };

  const getGoalsByStatus = (status) => {
    const col = COLUMNS.find(c => c.id === status);
    const allowed = col?.statuses || [status];
    return goals.filter(g => allowed.includes(g.status));
  };
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-amber-400 bg-amber-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-[#C6D4E8] bg-slate-500/20';
    }
  };

  const generatePathways = async () => {
    setGenerating('pathways');
    try {
      const response = await axios.post(`${API}/ai/pathway-match`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 60000
      });
      setPathways(response.data.result);
      refreshUser();
      toast.success('Your personalized pathways are ready!');
    } catch (error) {
      console.error('Pathway error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        showFriendlyError(error, { fallback: 'Failed to generate pathways. Please try again.' });
      }
    } finally {
      setGenerating(null);
    }
  };

  const generateActionPlan = async () => {
    setGenerating('plan');
    try {
      const response = await axios.post(`${API}/ai/action-plan`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 60000
      });
      setActionPlan(response.data.result);
      refreshUser();
      toast.success('Your weekly action plan is ready!');
    } catch (error) {
      console.error('Plan error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        showFriendlyError(error, { fallback: 'Failed to generate plan. Please try again.' });
      }
    } finally {
      setGenerating(null);
    }
  };

  const generateScholarships = async () => {
    setGenerating('scholarships');
    try {
      const response = await axios.post(`${API}/ai/scholarship-match`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 60000
      });
      setScholarships(response.data.result);
      refreshUser();
      toast.success('Scholarships matching your profile found!');
    } catch (error) {
      console.error('Scholarship error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        showFriendlyError(error, { fallback: 'Failed to find scholarships. Please try again.' });
      }
    } finally {
      setGenerating(null);
    }
  };

  const addCertification = async () => {
    if (!newCert.name || !newCert.issuer || !newCert.date_earned) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/certifications`, newCert, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Certification added!');
      setShowAddCert(false);
      setNewCert({ name: '', issuer: '', date_earned: '' });
      fetchAllData();
    } catch (error) {
      showFriendlyError(error, { context: 'Add certification', fallback: "We couldn't save that certification. Please try again." });
    }
  };

  const addTestScore = async () => {
    if (!newScore.test_type || !newScore.score || !newScore.date_taken) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/test-scores`, {
        ...newScore,
        score: parseInt(newScore.score)
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Test score added!');
      setShowAddScore(false);
      setNewScore({ test_type: 'SAT', score: '', date_taken: '' });
      fetchAllData();
    } catch (error) {
      showFriendlyError(error, { context: 'Add test score', fallback: "We couldn't save that test score. Please try again." });
    }
  };

  const analyzeTranscript = async () => {
    setAnalyzingTranscript(true);
    try {
      const response = await axios.post(`${API}/ai/analyze-transcript`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 60000
      });
      toast.success('Transcript analyzed! Your profile has been updated.');
      setProfileStrength(response.data.profile_strength);
      fetchAllData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(
          <div>
            <p className="font-medium">No transcript found</p>
            <p className="text-sm">Upload a transcript in Document Vault first, then come back to analyze it.</p>
          </div>,
          {
            duration: 5000,
            action: {
              label: 'Go to Vault',
              onClick: () => window.location.href = '/app/vault'
            }
          }
        );
      } else {
        showFriendlyError(error, { context: 'Transcript', fallback: "We couldn't analyze that transcript. Please check the file and try again." });
      }
    } finally {
      setAnalyzingTranscript(false);
    }
  };

  const verifyPhone = async () => {
    if (!phoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }
    try {
      await axios.post(`${API}/profile/verify-phone`, {
        phone_number: phoneNumber
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Phone number saved!');
      fetchAllData();
    } catch (error) {
      showFriendlyError(error, { context: 'Save phone', fallback: "We couldn't save your phone number. Please try again." });
    }
  };

  // Tab configuration, District Requirements inserted at position 2 for institutional students
  const isInstitutional = !!(user?.org_id || user?.organization_id);
  const tabs = [
    ...(isInstitutional
      ? [{ id: 'district', label: 'District Requirements', icon: Building }]
      : [{ id: 'overview', label: 'Overview', icon: TrendingUp }]),
    { id: 'goals',     label: 'Goals & Action Plans', icon: Target },
    { id: 'financial', label: 'Financial Planning',   icon: Wallet },
    { id: 'personality',label:'Personality',          icon: Brain },
    { id: 'achievements',label:'Achievements',        icon: Award },
    { id: 'matches',   label: 'Career Matches',       icon: Briefcase },
    { id: 'timeline',  label: 'Progress',             icon: Clock },
    { id: 'security',  label: 'Security',             icon: Shield }
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Guided Tour */}
      <GuidedTour isOpen={showTour} onClose={closeTour} userRole="student" />
      
      {/* Greet them first, and name what they're in the middle of. The path they chose and the
          walk-through live in the Your Path bar above, one greeting, one place for context. */}
      <WelcomeHeader firstVisit={isFirstVisit} />
      <PathTourInvite />

      {/* The starting point, what the student entered high school with (NC 8th-grade CDP).
          Renders quietly (returns null) if the student has no NC CDP on record. */}
      <NCCDPHeroCard />

      {/* If recent exploration is pointing somewhere different from the current CDP
          direction, offer the student a chance to confirm or dismiss. */}
      <EvolutionCard />

      {/* Where they stand, the score the whole plan is measured by */}
      <div className="mb-4">
        <ReadinessMini />
      </div>

      {/* Self-signup students have no district requirements section, so the journey block
          stays for them. District students see requirements instead. */}
      {!isInstitutional && (
        <div className="mb-8">
          <MissionControl />
        </div>
      )}

      {/* Intelligence Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 scrollbar-none border-b border-[rgba(57,227,220,0.1)]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-3.5 py-2.5 text-xs font-medium flex items-center gap-1.5 transition-all whitespace-nowrap shrink-0 relative ${
                activeTab === tab.id
                  ? 'text-[#39E3DC]'
                  : 'text-[#C6D4E8] hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t" style={{ background: 'linear-gradient(90deg, #39E3DC, #782FE1)', boxShadow: '0 0 10px rgba(57,227,220,0.6)' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === 'overview' && (
        <>
          <FocusModeBanner />

          <ReportGenerator 
            userData={user}
            pathways={pathways}
            scholarships={scholarships}
            actionPlan={actionPlan}
            colleges={colleges}
            roadmap90Day={roadmap90Day}
          />


          {/* OBBBA Workforce Pell discovery, surfaces funded blue-collar paths early */}
          <div className="mb-8">
            <WorkforcePellCallout />
          </div>


          {/* News section removed, journey-focused dashboard */}
        </>
      )}

      {/* ==================== DISTRICT REQUIREMENTS TAB ==================== */}
      {activeTab === 'district' && <DistrictRequirementsTab />}

      {/* What is actually moving: deadlines, follow-ups and the launch step */}
      <NudgePanel />

      <div className="mb-4">
        <PlanNextActions />
      </div>

      {/* Patricia Page Guide, after the plan, not in front of it */}
      <div className="mt-8 mb-6">
        <PatriciaPageGuide pageId="dashboard" />
      </div>

      {/* ==================== GOALS & ACTION PLANS TAB ==================== */}
      {activeTab === 'goals' && (
        <div className="space-y-6">
          {/* Header with Add Goal button */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white font-outfit flex items-center gap-3">
                <Target className="w-7 h-7 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.5))' }} />
                Goals & Action Plans
              </h2>
              <p className="text-[#C6D4E8] mt-1">Drag and drop goals between columns to track your progress</p>
            </div>
            <Button onClick={() => setShowAddGoalModal(true)} data-testid="add-goal-btn"
              style={{ background: 'linear-gradient(90deg, #352CA0, #782FE1)', boxShadow: '0 0 16px rgba(120,47,225,0.3)' }}>
              <Plus className="w-4 h-4 mr-2" /> Add Goal
            </Button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4">
            {COLUMNS.map(col => {
              const Icon = col.icon;
              const count = getGoalsByStatus(col.id).length;
              return (
                <GlassCard key={col.id} className="text-center">
                  <Icon className={`w-6 h-6 mx-auto mb-2 text-${col.color}-400`} />
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-sm text-[#C6D4E8]">{col.label}</p>
                </GlassCard>
              );
            })}
          </div>

          {/* Kanban Board */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COLUMNS.map(column => {
              const Icon = column.icon;
              const columnGoals = getGoalsByStatus(column.id);
              
              return (
                <div 
                  key={column.id}
                  className={`rounded-xl p-4 min-h-[400px] transition-all ${
                    draggedGoal ? 'border-dashed' : ''
                  }`}
                  style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.15)', boxShadow: draggedGoal ? '0 0 12px rgba(57,227,220,0.1)' : 'none' }}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(column.id)}
                  data-testid={`column-${column.id}`}
                >
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[rgba(57,227,220,0.1)]">
                    <Icon className={`w-5 h-5 text-${column.color}-400`} />
                    <h3 className="font-semibold text-white">{column.label}</h3>
                    <span className="ml-auto text-[#C6D4E8] text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(57,227,220,0.06)', border: '1px solid rgba(57,227,220,0.15)' }}>{columnGoals.length}</span>
                  </div>

                  <div className="space-y-3">
                    {columnGoals.map(goal => {
                      const category = CATEGORIES[goal.category] || CATEGORIES.personal;
                      const CategoryIcon = category.icon;
                      
                      return (
                        <div
                          key={goal.id}
                          draggable
                          onDragStart={() => handleDragStart(goal)}
                          className={`bg-slate-900/80 rounded-lg p-4 border border-slate-600 cursor-grab hover:border-blue-500/50 transition-all ${
                            draggedGoal?.id === goal.id ? 'opacity-50' : ''
                          }`}
                          data-testid={`goal-card-${goal.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <CategoryIcon className={`w-4 h-4 text-${category.color}-400`} />
                              <span className={`text-xs px-2 py-0.5 rounded-full bg-${category.color}-500/20 text-${category.color}-400`}>
                                {category.label}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(goal.priority)}`}>
                              {goal.priority}
                            </span>
                          </div>
                          
                          <h4 className="text-white font-medium mb-1">{goal.title}</h4>
                          {goal.description && (
                            <p className="text-sm text-[#C6D4E8] mb-2 line-clamp-2">{goal.description}</p>
                          )}
                          
                          {goal.target_date && (
                            <div className="flex items-center gap-1 text-xs text-slate-300 mb-2">
                              <Calendar className="w-3 h-3" />
                              {new Date(goal.target_date).toLocaleDateString()}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[rgba(57,227,220,0.12)]">
                            {column.id !== 'completed' && (
                              <button
                                onClick={() => updateGoalStatus(goal.id, column.id === 'recommended' ? 'in_progress' : 'completed')}
                                className="text-xs text-[#39E3DC] hover:text-blue-300 flex items-center gap-1"
                              >
                                <ChevronRight className="w-3 h-3" />
                                {column.id === 'recommended' ? 'Start' : 'Complete'}
                              </button>
                            )}
                            <button onClick={() => deleteGoal(goal.id)} className="text-xs text-red-400 hover:text-red-300 ml-auto">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    
                    {columnGoals.length === 0 && (
                      <div className="text-center py-8 text-slate-300">
                        <Icon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No goals here</p>
                        {column.id === 'recommended' && (
                          <button onClick={() => setShowAddGoalModal(true)} className="text-[#39E3DC] text-sm mt-2 hover:underline">
                            Add your first goal
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action Plan Section */}
          <GlassCard>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-outfit text-xl font-semibold text-white">AI-Generated Weekly Actions</h3>
                <p className="text-slate-300 text-sm mt-1">Actions pulled from your goals and profile</p>
              </div>
              <Button onClick={generateActionPlan} disabled={generating === 'plan'} className="bg-purple-600 hover:bg-purple-700" data-testid="generate-ai-plan-btn">
                {generating === 'plan' ? <LoadingSpinner size="sm" /> : <><Sparkles className="w-4 h-4 mr-2" /> Generate AI Plan</>}
              </Button>
            </div>

            {actionPlan?.actions ? (
              <div className="space-y-3">
                {actionPlan.week_focus && (
                  <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20 mb-4">
                    <p className="text-cyan-400 font-medium text-sm">🎯 Week Focus: {actionPlan.week_focus}</p>
                  </div>
                )}
                {actionPlan.actions.map((action, index) => (
                  <ActionPlanCard key={index} action={action} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#C6D4E8]">
                <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Generate an AI action plan based on your goals</p>
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* ==================== FINANCIAL PLANNING TAB ==================== */}
      {activeTab === 'financial' && (
        <div className="space-y-6">
          {/* Financial Disclaimer (compact) */}
          {!disclaimerAccepted && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/8 border border-amber-500/15" data-testid="financial-disclaimer">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-500/60 shrink-0" />
              <p className="text-amber-400/60 text-[10px] flex-1">
                For <span className="underline decoration-dotted cursor-help" title="The information provided is for educational and modeling purposes only and does not constitute financial advice. We recommend consulting with a qualified financial advisor or college financial aid counselor before making decisions.">educational purposes only</span>, not financial advice.
              </p>
              <button onClick={() => setDisclaimerAccepted(true)} className="text-amber-400 text-[10px] font-medium hover:text-amber-300 shrink-0" data-testid="accept-disclaimer-btn">Dismiss</button>
            </div>
          )}

          {/* Financial Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <GlassCard className="text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-[#39E3DC]" />
              <p className="text-3xl font-bold text-white">{formatCurrency(financialData?.estimated_cost || 0)}</p>
              <p className="text-sm text-[#C6D4E8]">4-Year Cost Estimate</p>
            </GlassCard>
            <GlassCard className="text-center">
              <Award className="w-8 h-8 mx-auto mb-2 text-green-400" />
              <p className="text-3xl font-bold text-white">{scholarships?.scholarships?.length || 0}</p>
              <p className="text-sm text-[#C6D4E8]">Scholarships Found</p>
            </GlassCard>
            <GlassCard className="text-center">
              <PiggyBank className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-3xl font-bold text-white">{formatCurrency(financialData?.potential_aid || 0)}</p>
              <p className="text-sm text-[#C6D4E8]">Potential Aid</p>
            </GlassCard>
            <GlassCard className="text-center">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
              <p className="text-3xl font-bold text-white">{formatCurrency(financialData?.funding_gap || 0)}</p>
              <p className="text-sm text-[#C6D4E8]">Funding Gap</p>
            </GlassCard>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/app/financial">
              <GlassCard className="hover:border-blue-500/50 transition-all cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[rgba(57,227,220,0.12)] flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-[#39E3DC]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Real Cost Calculator</h3>
                    <p className="text-[#C6D4E8] text-sm">See what you'll actually pay</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 ml-auto" />
                </div>
              </GlassCard>
            </Link>
            
            <Link to="/app/scholarships">
              <GlassCard className="hover:border-green-500/50 transition-all cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Award className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Scholarship Finder</h3>
                    <p className="text-[#C6D4E8] text-sm">Match with scholarships</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 ml-auto" />
                </div>
              </GlassCard>
            </Link>
            
            <Link to="/app/calculator">
              <GlassCard className="hover:border-purple-500/50 transition-all cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Lifestyle Calculator</h3>
                    <p className="text-[#C6D4E8] text-sm">Plan your future earnings</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 ml-auto" />
                </div>
              </GlassCard>
            </Link>
          </div>

          {/* Scholarships Section */}
          <GlassCard>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-outfit text-xl font-semibold text-white">Matched Scholarships</h3>
                <p className="text-[#C6D4E8] text-sm">Based on your profile and goals</p>
              </div>
              <Button onClick={generateScholarships} disabled={generating === 'scholarships'} className="bg-green-600 hover:bg-green-700" data-testid="find-scholarships-btn">
                {generating === 'scholarships' ? <LoadingSpinner size="sm" /> : <><Award className="w-4 h-4 mr-2" /> Find Scholarships</>}
              </Button>
            </div>

            {scholarships?.scholarships ? (
              <div className="grid md:grid-cols-2 gap-4">
                {scholarships.scholarships.map((scholarship, index) => (
                  <ScholarshipCard key={index} scholarship={scholarship} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#C6D4E8]">
                <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Click "Find Scholarships" to discover funding opportunities</p>
              </div>
            )}
          </GlassCard>

          {/* Full Financial Intelligence Link */}
          <Link to="/app/financial">
            <div className="p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl border border-blue-500/30 hover:border-blue-500/50 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[rgba(57,227,220,0.12)] flex items-center justify-center">
                    <Wallet className="w-7 h-7 text-[#39E3DC]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Full Financial Intelligence</h3>
                    <p className="text-[#C6D4E8]">Access detailed cost breakdowns, funding plans, and loan calculators</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-[#39E3DC]" />
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* ==================== PERSONALITY TAB ==================== */}
      {activeTab === 'personality' && (
        <div className="space-y-6">
          {personalitySummary?.has_personality ? (
            <>
              <GlassCard>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="font-outfit text-2xl font-bold text-white">{personalitySummary.personality_type?.name}</h2>
                    <p className="text-[#C6D4E8]">{personalitySummary.personality_type?.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <h3 className="text-emerald-400 font-medium mb-2 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Strengths</h3>
                    <ul className="space-y-1">
                      {personalitySummary.personality_type?.strengths?.map((s, i) => (
                        <li key={i} className="text-slate-300 text-sm">• {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <h3 className="text-amber-400 font-medium mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Growth Areas</h3>
                    <ul className="space-y-1">
                      {personalitySummary.personality_type?.growth_areas?.map((g, i) => (
                        <li key={i} className="text-slate-300 text-sm">• {g}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {personalitySummary.learning_style && (
                  <div className="p-4 bg-[rgba(57,227,220,0.08)] rounded-lg border border-[rgba(57,227,220,0.2)]">
                    <h3 className="text-[#39E3DC] font-medium mb-2">
                      Learning Style: {personalitySummary.learning_style.primary?.charAt(0).toUpperCase() + personalitySummary.learning_style.primary?.slice(1)}
                    </h3>
                    <ul className="space-y-1">
                      {personalitySummary.learning_style.tips?.map((tip, i) => (
                        <li key={i} className="text-slate-300 text-sm">• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </GlassCard>

              {personalitySummary.trait_scores && (
                <GlassCard>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-4">Trait Analysis</h3>
                  <div className="space-y-4">
                    {Object.entries(personalitySummary.trait_scores).map(([trait, score]) => (
                      <div key={trait}>
                        <div className="flex justify-between mb-1">
                          <span className="text-slate-300 capitalize">{trait}</span>
                          <span className="text-purple-400 font-semibold">{score}%</span>
                        </div>
                        <div className="h-3 bg-[rgba(6,23,54,0.5)] rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${score >= 70 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : score >= 40 ? 'bg-gradient-to-r from-blue-500 to-blue-400' : 'bg-gradient-to-r from-amber-500 to-amber-400'}`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              )}

              <div className="text-center">
                <Link to="/app/assessment">
                  <Button variant="outline" className="border-purple-500/50 text-purple-300">
                    <Brain className="w-4 h-4 mr-2" /> Retake Personality Quiz
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <GlassCard className="text-center py-12">
              <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Personality Profile Yet</h3>
              <p className="text-[#C6D4E8] mb-6 max-w-md mx-auto">
                Take the personality quiz to discover your unique traits, ideal work environments, and career matches tailored to you.
              </p>
              <Link to="/app/assessment">
                <Button className="bg-purple-600 hover:bg-purple-700"><Brain className="w-4 h-4 mr-2" /> Start Personality Quiz</Button>
              </Link>
            </GlassCard>
          )}
        </div>
      )}

      {/* ==================== ACHIEVEMENTS TAB ==================== */}
      {activeTab === 'achievements' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit text-lg font-semibold text-white">Certifications</h3>
              <Button onClick={() => setShowAddCert(true)} size="sm" className="bg-amber-600" data-testid="add-cert-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {certifications.length > 0 ? (
              <div className="space-y-3">
                {certifications.map((cert) => (
                  <div key={cert.id} className="p-3 bg-[rgba(6,23,54,0.72)] rounded-lg flex items-start gap-3">
                    <Award className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white font-medium">{cert.name}</p>
                      <p className="text-[#C6D4E8] text-sm">{cert.issuer}</p>
                      <p className="text-slate-300 text-xs">{cert.date_earned}</p>
                    </div>
                    {cert.credential_url && (
                      <a href={cert.credential_url} target="_blank" rel="noopener noreferrer" className="text-[#39E3DC] hover:text-blue-300">
                        <Eye className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-[#C6D4E8]">No certifications yet</p>
                <p className="text-slate-300 text-sm">Add your Coursera, LinkedIn Learning, or other certs</p>
              </div>
            )}
          </GlassCard>

          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit text-lg font-semibold text-white">Test Scores</h3>
              <Button onClick={() => setShowAddScore(true)} size="sm" className="bg-blue-600" data-testid="add-score-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {testScores.length > 0 ? (
              <div className="space-y-3">
                {testScores.map((score) => (
                  <div key={score.id} className="p-3 bg-[rgba(6,23,54,0.72)] rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ClipboardCheck className="w-5 h-5 text-[#39E3DC]" />
                      <div>
                        <p className="text-white font-medium">{score.test_type}</p>
                        <p className="text-slate-300 text-xs">{score.date_taken}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#39E3DC]">{score.score}</p>
                      {score.max_score && <p className="text-slate-300 text-xs">/ {score.max_score}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-[#C6D4E8]">No test scores yet</p>
                <p className="text-slate-300 text-sm">Add SAT, ACT, ASVAB, or AP scores</p>
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* ==================== CAREER MATCHES TAB ==================== */}
      {activeTab === 'matches' && (
        <div className="space-y-6">
          {user?.career_aspirations && (
            <div className="flex items-center gap-3 rounded-xl bg-amber-500/10 border border-amber-500/25 px-4 py-3" data-testid="matches-career-goal">
              <Briefcase className="w-5 h-5 text-amber-300 flex-shrink-0" />
              <p className="text-sm text-slate-200">
                <span className="text-amber-300 font-semibold">Career Goal:</span> {user.career_aspirations}
                <span className="text-[#C6D4E8]">, change it anytime in <Link to="/app/profile" className="underline hover:text-white">Settings</Link>.</span>
              </p>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {careerMatches && careerMatches.map((c, idx) => {
              const score = c.match_score ?? c.score ?? 0;
              return (
                <GlassCard key={idx} className={score >= 70 ? 'border-emerald-500/30' : ''} data-testid={`career-match-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-500/20">
                        <Briefcase className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-lg">{c.title || c.career || c.name}</h4>
                        <p className="text-[#C6D4E8] text-xs">Matched to your personality &amp; strengths</p>
                      </div>
                    </div>
                    <div className={`text-2xl font-bold ${score >= 70 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-[#C6D4E8]'}`}>
                      {score}%
                    </div>
                  </div>
                  {(c.why_match || c.reason || c.description) && (
                    <p className="text-slate-300 text-sm mb-3">{c.why_match || c.reason || c.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs">
                    {c.salary_range && (
                      <span className="px-2.5 py-1 rounded-full bg-[rgba(6,23,54,0.5)]/50 text-slate-200">💰 {c.salary_range}</span>
                    )}
                    {(c.education_path || c.education) && (
                      <span className="px-2.5 py-1 rounded-full bg-[rgba(6,23,54,0.5)]/50 text-slate-200">🎓 {c.education_path || c.education}</span>
                    )}
                    {c.growth_outlook && (
                      <span className="px-2.5 py-1 rounded-full bg-[rgba(6,23,54,0.5)]/50 text-slate-200">📈 {c.growth_outlook}</span>
                    )}
                  </div>
                  <Link to="/app/careers">
                    <Button variant="ghost" size="sm" className="mt-4 text-[#39E3DC] hover:text-blue-300 px-0" data-testid={`explore-match-${idx}`}>
                      Explore this career <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </GlassCard>
              );
            })}

            {(!careerMatches || careerMatches.length === 0) && (
              <GlassCard className="lg:col-span-2 text-center py-12">
                <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Career Matches Yet</h3>
                <p className="text-[#C6D4E8] mb-6 max-w-md mx-auto">Take the personality quiz to see careers matched to your strengths and interests.</p>
                <Link to="/app/assessment">
                  <Button className="bg-blue-600 hover:bg-blue-700"><Brain className="w-4 h-4 mr-2" /> Take Assessment</Button>
                </Link>
              </GlassCard>
            )}
          </div>
        </div>
      )}

      {/* ==================== TIMELINE TAB ==================== */}
      {activeTab === 'timeline' && (
        <GlassCard>
          <h3 className="font-outfit text-lg font-semibold text-white mb-6">Your Journey</h3>
          {timeline.length > 0 ? (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[rgba(6,23,54,0.5)]" />
              <div className="space-y-4">
                {timeline.map((event, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }} className="flex items-start gap-4 pl-8 relative">
                    <div className={`absolute left-2 w-4 h-4 rounded-full ${event.type === 'certification' ? 'bg-amber-500' : event.type === 'test_score' ? 'bg-blue-500' : event.type === 'milestone' ? 'bg-emerald-500' : event.type === 'achievement' ? 'bg-purple-500' : 'bg-slate-500'}`} />
                    <div className="flex-1 p-3 bg-[rgba(6,23,54,0.72)] rounded-lg">
                      <p className="text-white font-medium">{event.title}</p>
                      <p className="text-slate-300 text-xs">{event.date?.split('T')[0]}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-[#C6D4E8]">Your journey is just beginning</p>
              <p className="text-slate-300 text-sm">Complete actions to build your timeline</p>
            </div>
          )}
        </GlassCard>
      )}

      {/* ==================== SECURITY TAB ==================== */}
      {activeTab === 'security' && security && (
        <div className="grid lg:grid-cols-2 gap-6">
          <GlassCard>
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-emerald-400" />
              <div>
                <h3 className="font-outfit text-lg font-semibold text-white">Your Data is Protected</h3>
                <p className="text-[#C6D4E8] text-sm">We take your privacy seriously</p>
              </div>
            </div>
            <div className="space-y-3">
              {security.security_features?.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-white text-sm font-medium">{feature.feature}</p>
                    <p className="text-[#C6D4E8] text-xs">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">Data Stored</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-[rgba(6,23,54,0.72)] rounded-lg">
                <span className="text-slate-300">Documents</span>
                <span className="text-[#39E3DC] font-bold">{security.data_stored?.documents || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-[rgba(6,23,54,0.72)] rounded-lg">
                <span className="text-slate-300">Certifications</span>
                <span className="text-amber-400 font-bold">{security.data_stored?.certifications || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-[rgba(6,23,54,0.72)] rounded-lg">
                <span className="text-slate-300">Test Scores</span>
                <span className="text-emerald-400 font-bold">{security.data_stored?.test_scores || 0}</span>
              </div>
            </div>
            <div className="mt-6 p-4 bg-slate-800/30 rounded-lg">
              <h4 className="text-white font-medium mb-2">Data Policy</h4>
              <p className="text-[#C6D4E8] text-sm">{security.data_retention?.policy}</p>
              <p className="text-slate-300 text-xs mt-2">{security.data_retention?.deletion}</p>
            </div>
          </GlassCard>
        </div>
      )}

      {/* ==================== MODALS ==================== */}
      
      {/* Add Goal Modal */}
      <AnimatePresence>
        {showAddGoalModal && (
          <div className="fixed inset-0 bg-[#0a1729]/60 flex items-center justify-center z-50 p-4" onClick={() => setShowAddGoalModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-600" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Add New Goal</h3>
                <button onClick={() => setShowAddGoalModal(false)} className="text-[#C6D4E8] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#C6D4E8] mb-2">Goal Title *</label>
                  <Input value={newGoal.title} onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })} className="input-dark" placeholder="e.g., Apply to MIT, Learn Python" data-testid="goal-title-input" />
                </div>
                <div>
                  <label className="block text-sm text-[#C6D4E8] mb-2">Description</label>
                  <textarea value={newGoal.description} onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })} className="w-full bg-slate-900 border border-[rgba(57,227,220,0.12)] rounded-lg py-3 px-4 text-white focus:outline-none focus:border-blue-500 h-20" placeholder="Add details..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#C6D4E8] mb-2">Category</label>
                    <select value={newGoal.category} onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })} className="w-full bg-slate-900 border border-[rgba(57,227,220,0.12)] rounded-lg py-3 px-4 text-white">
                      {Object.entries(CATEGORIES).map(([key, cat]) => (<option key={key} value={key}>{cat.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#C6D4E8] mb-2">Priority</label>
                    <select value={newGoal.priority} onChange={(e) => setNewGoal({ ...newGoal, priority: e.target.value })} className="w-full bg-slate-900 border border-[rgba(57,227,220,0.12)] rounded-lg py-3 px-4 text-white">
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#C6D4E8] mb-2">Target Date</label>
                  <Input type="date" value={newGoal.target_date} onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })} className="input-dark" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddGoalModal(false)} className="flex-1 border-slate-600">Cancel</Button>
                <Button onClick={addGoal} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-goal-btn">Add to Backlog</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Certification Modal */}
      <AnimatePresence>
        {showAddCert && (
          <div className="fixed inset-0 bg-[#0a1729]/50 flex items-center justify-center z-50" onClick={() => setShowAddCert(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">Add Certification</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#C6D4E8] block mb-1">Certification Name *</label>
                  <Input placeholder="e.g., Google IT Support Professional" value={newCert.name} onChange={(e) => setNewCert({...newCert, name: e.target.value})} className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-[#C6D4E8] block mb-1">Issuer *</label>
                  <Input placeholder="e.g., Coursera, LinkedIn Learning" value={newCert.issuer} onChange={(e) => setNewCert({...newCert, issuer: e.target.value})} className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-[#C6D4E8] block mb-1">Date Earned *</label>
                  <Input type="date" value={newCert.date_earned} onChange={(e) => setNewCert({...newCert, date_earned: e.target.value})} className="input-dark" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddCert(false)} className="border-[rgba(57,227,220,0.12)]">Cancel</Button>
                  <Button onClick={addCertification} className="flex-1 bg-amber-600 hover:bg-amber-700">Add Certification</Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Test Score Modal */}
      <AnimatePresence>
        {showAddScore && (
          <div className="fixed inset-0 bg-[#0a1729]/50 flex items-center justify-center z-50" onClick={() => setShowAddScore(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">Add Test Score</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#C6D4E8] block mb-1">Test Type *</label>
                  <select value={newScore.test_type} onChange={(e) => setNewScore({...newScore, test_type: e.target.value})} className="w-full bg-slate-900 border border-[rgba(57,227,220,0.12)] rounded-lg px-3 py-2 text-white">
                    <option value="SAT">SAT</option>
                    <option value="ACT">ACT</option>
                    <option value="PSAT">PSAT</option>
                    <option value="ASVAB">ASVAB</option>
                    <option value="AP">AP Exam</option>
                    <option value="GED">GED</option>
                    <option value="TEAS">TEAS</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-[#C6D4E8] block mb-1">Score *</label>
                  <Input type="number" placeholder="e.g., 1400" value={newScore.score} onChange={(e) => setNewScore({...newScore, score: e.target.value})} className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-[#C6D4E8] block mb-1">Date Taken *</label>
                  <Input type="date" value={newScore.date_taken} onChange={(e) => setNewScore({...newScore, date_taken: e.target.value})} className="input-dark" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddScore(false)} className="border-[rgba(57,227,220,0.12)]">Cancel</Button>
                  <Button onClick={addTestScore} className="flex-1 bg-blue-600 hover:bg-blue-700">Add Score</Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
